import { useState, useCallback } from 'react';
import { Input, Select, Table, Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import CustomDateRangePicker from "../../components/customDateRangePicker";
import { fetchPayments } from '../../apis/paymentsApi';
import { formatCurrency } from '../../utils/Formating';
import moment from 'moment';

const { Title } = Typography;

const AccountingDetails = () => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [totalAmount, setTotalAmount] = useState('');
  const [searchedId, setSearchedId] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryNames, setCategoryNames] = useState([]);
  const [paymentMethodsNames, setPaymentMethodsNames] = useState([]);
  const [selectedpaymentMethods, setSelectedPaymentMethods] = useState('');

  const fetchAccountingClose = async (startDate, endDate) => {
    const result = await fetchPayments(startDate, endDate);
    setData(result.sales || []);
    setOriginalData(result.sales || []);
    setTotalAmount(result.totalAmount || 0);

    const uniqueCategories = Array.from(new Set(result.sales.map(s => s.category_name).filter(Boolean)));
    setCategoryNames(uniqueCategories);

    const uniquePaymentMethods = Array.from(new Set(result.sales.map(s => s.payment_method_name).filter(Boolean)));
    setPaymentMethodsNames(uniquePaymentMethods);
  };

  const applyFilters = useCallback(
    (id, category_name, payment_method_name) => {
      const filteredVals = originalData.filter((entry) => {
        const matchesId = entry.id?.toString().includes(id);

        const matchesCategory = category_name ? entry.category_name === category_name : true;

        const matchesPaymentMethod = payment_method_name ? entry.payment_method_name === payment_method_name : true;

        return matchesId && matchesCategory && matchesPaymentMethod;
      });

      setData(filteredVals);
    },
    [originalData]
  );

  const handleSearchedIdChange = useCallback(
    (e) => {
      const id = e.target.value;
      setSearchedId(id);
      applyFilters(id, selectedCategory, selectedpaymentMethods);
    },
    [selectedCategory, selectedpaymentMethods, applyFilters]
  );

  const handleCategoryChange = useCallback(
    (value) => {
      const category = value
      setSelectedCategory(category);
      applyFilters(searchedId, category, selectedpaymentMethods);
    },
    [searchedId, selectedpaymentMethods, applyFilters]
  );

  const handlePaymentMethodChange = useCallback(
    (value) => {
      const paymentMethod = value
      setSelectedPaymentMethods(paymentMethod);
      applyFilters(searchedId, selectedCategory, paymentMethod);
    },
    [searchedId, selectedCategory, applyFilters]
  );

  const columns = [
    {
      title: (
        <div>
          <p>Id</p>
          <Input
            placeholder="Buscar ID"
            value={searchedId}
            onChange={handleSearchedIdChange}
            style={{ width: 100 }}
          />
        </div>
      ),
      dataIndex: 'id',
      key: 'id',
      align: 'center',
    },
    {
      title: (
        <div>
          <p>Método de Pago</p>
          <Select
            allowClear
            placeholder="Filtrar método de pago"
            style={{ width: '100%' }}
            value={selectedpaymentMethods || undefined}
            onChange={handlePaymentMethodChange}
            options={paymentMethodsNames.map(p => ({
              value: p,
              label: p,
            }))}
          />
        </div>
      ),
      dataIndex: 'payment_method_name',
      key: 'payment_method_name',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Monto",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: (amount) =>
          amount !== null && amount !== undefined && amount !== ""
          ? formatCurrency(amount) : "N/A",
      sorter: {
        compare: (a, b) =>
          a.amount - b.amount,
        multiple: 1,
      },
    },
    {
      title: (
        <div>
          <p>Categoría</p>
          <Select
            allowClear
            placeholder="Filtrar categoría"
            style={{ width: '100%' }}
            value={selectedCategory || undefined}
            onChange={handleCategoryChange}
            options={categoryNames.map(p => ({
              value: p,
              label: p,
            }))}
          />
        </div>
      ),
      dataIndex: 'category_name',
      key: 'category_name',
      align: 'center',
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Fecha Registro",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (date) =>
        date ? moment.utc(date).local().format("DD-MM-YYYY HH:mm:ss") : "N/A", // Formatea la fecha si es válida
      sorter: {
        compare: (a, b) =>
        moment(a.created_at).startOf('day').diff(moment(b.created_at).startOf('day')),
        multiple: 2,
      },
    },
    {
      title: "Info",
      dataIndex: "data",
      key: "data",
      align: "center",
      render: (info, record, idx) => {
        const isExpanded = expandedRow === idx;
        return (
          <div
            style={{
              maxWidth: isExpanded ? "none" : 200,
              whiteSpace: isExpanded ? "normal" : "nowrap",
              overflow: isExpanded ? "visible" : "hidden",
              textOverflow: isExpanded ? "clip" : "ellipsis",
              cursor: "pointer",
              transition: "max-width 0.2s",
              wordBreak: "break-all",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            onClick={() => setExpandedRow(isExpanded ? null : idx)}
            title={!isExpanded ? info : undefined}
          >
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
            <span>{info || "N/A"}</span>
          </div>
        );
      },
    },
  ];

  return (
    <div className='mobile-block themed-block p-5'>
      <Title level={2} className='text-center'>Transacciones</Title>
      <CustomDateRangePicker months={2} maxDate={new Date()} onDateChange={fetchAccountingClose} className="mb-5"/>
      {totalAmount ? <Title level={5} className='text-center'>Total: {formatCurrency(totalAmount)}</Title> : ''}
      <div className='table-responsive'>
        <Table
          dataSource={data.map((item, index) => ({ ...item, key: index }))}
          columns={columns}
          className="text-center"
        />
      </div>
    </div>
  );
};

export default AccountingDetails;
